import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  ArchiveBoxIcon,
  MagnifyingGlassIcon,
  ArrowUturnLeftIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { investorsService } from "../services/investorsService";
import {
  fetchDeletedINRInvestors,
  restoreINRInvestor,
  permanentlyDeleteINRInvestor,
} from "../services/inrInvestorsService";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const DeletedRecordsPage = () => {
  const [activeTab, setActiveTab] = useState("investors");
  const [investors, setInvestors] = useState([]);
  const [inrInvestors, setInrInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [investorsTotal, setInvestorsTotal] = useState(0);
  const [inrInvestorsTotal, setInrInvestorsTotal] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Bulk selection state (separate per tab so selections aren't lost on tab toggle)
  const [selectedInvestorIds, setSelectedInvestorIds] = useState(new Set());
  const [selectedInrIds, setSelectedInrIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  // Get user permissions from AuthContext
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === "admin";
  const hasInvestorsView = isAdmin || hasPermission("investors.view");
  const hasInrView = isAdmin || hasPermission("inr.view");

  // Determine which tabs the user can access
  const availableTabs = [];
  if (hasInvestorsView) {
    availableTabs.push("investors");
  }
  if (hasInrView) {
    availableTabs.push("inr-investors");
  }

  // Set default tab based on available permissions
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [hasInvestorsView, hasInrView]);

  const loadDeletedRecords = async (isSearchOperation = false) => {
    // Check if user has permission for the current tab
    if (activeTab === "investors" && !hasInvestorsView) {
      setError("You don't have permission to view deleted investor records");
      setLoading(false);
      setSearchLoading(false);
      return;
    }
    if (activeTab === "inr-investors" && !hasInrView) {
      setError(
        "You don't have permission to view deleted INR investor records"
      );
      setLoading(false);
      setSearchLoading(false);
      return;
    }

    if (isSearchOperation) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = {
        search: search.trim(),
        page,
        limit: pageSize,
        sortBy: "deleted_at",
        sortOrder: "DESC",
      };

      let result;
      if (activeTab === "investors") {
        result = await investorsService.fetchDeletedInvestors(params);
        setInvestors(result.data || []);
        setInvestorsTotal(result.pagination?.totalCount || 0);
      } else {
        result = await fetchDeletedINRInvestors(params);
        setInrInvestors(result.data || []);
        setInrInvestorsTotal(result.pagination?.totalCount || 0);
      }

      setTotal(result.pagination?.totalCount || 0);
    } catch (err) {
      console.error("Error loading deleted records:", err);
      setError(
        `Failed to load deleted records: ${
          err.response?.data?.error || err.message
        }`
      );
      if (activeTab === "investors") {
        setInvestors([]);
        setInvestorsTotal(0);
      } else {
        setInrInvestors([]);
        setInrInvestorsTotal(0);
      }
      setTotal(0);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Load counts for both tabs
  const loadTabCounts = async () => {
    try {
      const params = {
        search: search.trim(),
        page: 1,
        limit: 1, // We only need the count, not the data
        sortBy: "deleted_at",
        sortOrder: "DESC",
      };

      // Load investors count if user has permission
      if (hasInvestorsView) {
        try {
          const investorsResult = await investorsService.fetchDeletedInvestors(
            params
          );
          setInvestorsTotal(investorsResult.pagination?.totalCount || 0);
        } catch (err) {
          console.error("Error loading investors count:", err);
          setInvestorsTotal(0);
        }
      }

      // Load INR investors count if user has permission
      if (hasInrView) {
        try {
          const inrResult = await fetchDeletedINRInvestors(params);
          setInrInvestorsTotal(inrResult.pagination?.totalCount || 0);
        } catch (err) {
          console.error("Error loading INR investors count:", err);
          setInrInvestorsTotal(0);
        }
      }
    } catch (err) {
      console.error("Error loading tab counts:", err);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setPage(1);
      loadDeletedRecords(true);
      loadTabCounts(); // Also update tab counts
    }, 500);

    return () => {
      clearTimeout(searchTimeout);
      setSearchLoading(false);
    };
  }, [search, activeTab]);

  useEffect(() => {
    loadDeletedRecords();
  }, [page, pageSize, activeTab]);

  // Load initial tab counts
  useEffect(() => {
    if (hasInvestorsView || hasInrView) {
      loadTabCounts();
    }
  }, [hasInvestorsView, hasInrView]);

  const handleRestore = async (record) => {
    if (
      !window.confirm(
        `Are you sure you want to restore this ${
          activeTab === "investors" ? "investor" : "INR investor"
        } record?`
      )
    )
      return;

    try {
      if (activeTab === "investors") {
        await investorsService.restoreInvestor(record.id);
      } else {
        await restoreINRInvestor(record.id);
      }

      setPage(1);
      loadDeletedRecords();
      loadTabCounts(); // Update tab counts after restore
    } catch (err) {
      setError(
        `Failed to restore record: ${err.response?.data?.error || err.message}`
      );
    }
  };

  const handlePermanentDelete = async (record) => {
    if (
      !window.confirm(
        `Are you sure you want to PERMANENTLY delete this ${
          activeTab === "investors" ? "investor" : "INR investor"
        } record? This action cannot be undone.`
      )
    )
      return;

    try {
      if (activeTab === "investors") {
        await investorsService.permanentlyDeleteInvestor(record.id);
      } else {
        await permanentlyDeleteINRInvestor(record.id);
      }

      setPage(1);
      loadDeletedRecords();
      loadTabCounts(); // Update tab counts after permanent delete
    } catch (err) {
      setError(
        `Failed to permanently delete record: ${
          err.response?.data?.error || err.message
        }`
      );
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // ---------- Bulk selection helpers ----------
  const currentRecords = activeTab === "investors" ? investors : inrInvestors;
  const selectedIds =
    activeTab === "investors" ? selectedInvestorIds : selectedInrIds;
  const setSelectedIds =
    activeTab === "investors" ? setSelectedInvestorIds : setSelectedInrIds;

  const isRowSelected = (id) => selectedIds.has(id);

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allCurrentSelected =
    currentRecords.length > 0 &&
    currentRecords.every((r) => selectedIds.has(r.id));

  const toggleSelectAllCurrent = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentRecords.forEach((r) => next.delete(r.id));
      } else {
        currentRecords.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Clear selections when switching tab / page / search
  useEffect(() => {
    setSelectedInvestorIds(new Set());
    setSelectedInrIds(new Set());
  }, [activeTab]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, pageSize, search]);

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to restore ${selectedIds.size} selected ${
          activeTab === "investors" ? "investor" : "INR investor"
        } record(s)?`
      )
    )
      return;

    setBulkActionLoading(true);
    setError(null);
    const ids = Array.from(selectedIds);
    setBulkProgress({ done: 0, total: ids.length });
    const restoreFn =
      activeTab === "investors"
        ? investorsService.restoreInvestor.bind(investorsService)
        : restoreINRInvestor;

    // Run sequentially to avoid DB stored-procedure race conditions
    const failures = [];
    for (let i = 0; i < ids.length; i++) {
      try {
        await restoreFn(ids[i]);
      } catch (err) {
        failures.push({
          id: ids[i],
          message: err.response?.data?.error || err.message,
        });
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }

    if (failures.length > 0) {
      setError(
        `Restored ${ids.length - failures.length} of ${
          ids.length
        } record(s). ${failures.length} failed: ${failures
          .slice(0, 3)
          .map((f) => f.message)
          .join("; ")}${failures.length > 3 ? "..." : ""}`
      );
    }
    clearSelection();
    setPage(1);
    await loadDeletedRecords();
    await loadTabCounts();
    setBulkActionLoading(false);
    setBulkProgress({ done: 0, total: 0 });
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to PERMANENTLY delete ${selectedIds.size} selected ${
          activeTab === "investors" ? "investor" : "INR investor"
        } record(s)? This action cannot be undone.`
      )
    )
      return;

    setBulkActionLoading(true);
    setError(null);
    const ids = Array.from(selectedIds);
    setBulkProgress({ done: 0, total: ids.length });
    const deleteFn =
      activeTab === "investors"
        ? investorsService.permanentlyDeleteInvestor.bind(investorsService)
        : permanentlyDeleteINRInvestor;

    // Run sequentially to avoid DB stored-procedure race conditions
    const failures = [];
    for (let i = 0; i < ids.length; i++) {
      try {
        await deleteFn(ids[i]);
      } catch (err) {
        failures.push({
          id: ids[i],
          message: err.response?.data?.error || err.message,
        });
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }

    if (failures.length > 0) {
      setError(
        `Permanently deleted ${ids.length - failures.length} of ${
          ids.length
        } record(s). ${failures.length} failed: ${failures
          .slice(0, 3)
          .map((f) => f.message)
          .join("; ")}${failures.length > 3 ? "..." : ""}`
      );
    }
    clearSelection();
    setPage(1);
    await loadDeletedRecords();
    await loadTabCounts();
    setBulkActionLoading(false);
    setBulkProgress({ done: 0, total: 0 });
  };

  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const renderInvestorRow = (record) => {
    // Handle both string and object original_data
    const data =
      typeof record.original_data === "string"
        ? JSON.parse(record.original_data)
        : record.original_data;
    const schemeData = record.scheme_data
      ? typeof record.scheme_data === "string"
        ? JSON.parse(record.scheme_data)
        : record.scheme_data
      : null;

    return (
      <tr key={record.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={isRowSelected(record.id)}
            onChange={() => toggleRowSelection(record.id)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            aria-label={`Select ${data.buyer_name}`}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {data.buyer_name}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {data.mobile_number}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {schemeData?.scheme_name || "N/A"}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {data.status}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(record.deleted_at)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {record.deleted_by}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => showRecordDetails(record)}
              className="text-blue-600 hover:text-blue-900 p-1 rounded"
              title="View Details"
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleRestore(record)}
              className="text-green-600 hover:text-green-900 p-1 rounded"
              title="Restore"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handlePermanentDelete(record)}
              className="text-red-600 hover:text-red-900 p-1 rounded"
              title="Permanently Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderINRInvestorRow = (record) => {
    // Handle both string and object original_data
    const data =
      typeof record.original_data === "string"
        ? JSON.parse(record.original_data)
        : record.original_data;

    return (
      <tr key={record.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={isRowSelected(record.id)}
            onChange={() => toggleRowSelection(record.id)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            aria-label={`Select ${data.name}`}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {data.name}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {data.phone_number || "N/A"}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ₹{parseFloat(data.received || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ₹{parseFloat(data.sent || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ₹{parseFloat(data.balance || 0).toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(record.deleted_at)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {record.deleted_by}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => showRecordDetails(record)}
              className="text-blue-600 hover:text-blue-900 p-1 rounded"
              title="View Details"
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleRestore(record)}
              className="text-green-600 hover:text-green-900 p-1 rounded"
              title="Restore"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handlePermanentDelete(record)}
              className="text-red-600 hover:text-red-900 p-1 rounded"
              title="Permanently Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderDetailsModal = () => {
    if (!showDetailsModal || !selectedRecord) return null;

    // Handle both string and object original_data
    const data =
      typeof selectedRecord.original_data === "string"
        ? JSON.parse(selectedRecord.original_data)
        : selectedRecord.original_data;
    const schemeData = selectedRecord.scheme_data
      ? typeof selectedRecord.scheme_data === "string"
        ? JSON.parse(selectedRecord.scheme_data)
        : selectedRecord.scheme_data
      : null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Deleted Record Details
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Deletion Info */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5" />
                Deletion Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-red-800">Deleted At:</span>
                  <div className="text-red-700">
                    {formatDate(selectedRecord.deleted_at)}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-red-800">Deleted By:</span>
                  <div className="text-red-700">
                    {selectedRecord.deleted_by}
                  </div>
                </div>
                {selectedRecord.delete_reason && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-red-800">Reason:</span>
                    <div className="text-red-700">
                      {selectedRecord.delete_reason}
                    </div>
                  </div>
                )}
                {selectedRecord.restored_at && (
                  <div className="md:col-span-2 border-t border-red-300 pt-2">
                    <span className="font-medium text-green-800">
                      Previously Restored:
                    </span>
                    <div className="text-green-700">
                      {formatDate(selectedRecord.restored_at)} by{" "}
                      {selectedRecord.restored_by}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Original Data */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Original Record Data
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {activeTab === "investors" ? (
                  <>
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <div className="text-gray-600">{data.buyer_name}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Mobile:</span>
                      <div className="text-gray-600">{data.mobile_number}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Wallet:</span>
                      <div className="text-gray-600 break-all">
                        {data.wallet_address}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <div className="text-gray-600">{data.status}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Purchase Date:
                      </span>
                      <div className="text-gray-600">{data.purchase_date}</div>
                    </div>
                    {data.return_date && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Return Date:
                        </span>
                        <div className="text-gray-600">{data.return_date}</div>
                      </div>
                    )}
                    {schemeData && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Scheme:
                        </span>
                        <div className="text-gray-600">
                          {schemeData.scheme_name} ({schemeData.scheme_code})
                        </div>
                      </div>
                    )}
                    {data.usdt_received && (
                      <div>
                        <span className="font-medium text-gray-700">
                          USDT Received:
                        </span>
                        <div className="text-gray-600">
                          ${parseFloat(data.usdt_received).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <div className="text-gray-600">{data.name}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Phone:</span>
                      <div className="text-gray-600">
                        {data.phone_number || "N/A"}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Date:</span>
                      <div className="text-gray-600">{data.date}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Received:
                      </span>
                      <div className="text-gray-600">
                        ₹{parseFloat(data.received || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Sent:</span>
                      <div className="text-gray-600">
                        ₹{parseFloat(data.sent || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Balance:
                      </span>
                      <div className="text-gray-600">
                        ₹{parseFloat(data.balance || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Added By:
                      </span>
                      <div className="text-gray-600">{data.added_by}</div>
                    </div>
                    {data.remarks && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">
                          Remarks:
                        </span>
                        <div className="text-gray-600">{data.remarks}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between space-x-4">
              <button
                onClick={() => handleRestore(selectedRecord)}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Restore Record
              </button>
              <button
                onClick={() => handlePermanentDelete(selectedRecord)}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4" />
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <ArchiveBoxIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                Deleted Records
              </h1>
            </div>
            <p className="text-gray-600">
              Manage deleted records for both investors and INR investors. You
              can restore records or permanently delete them.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg mb-6">
          {availableTabs.length === 0 ? (
            <div className="p-6 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No Access to Deleted Records
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                You don't have permission to view any deleted records. Please
                contact your administrator to request access.
              </p>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200">
                <nav
                  className="-mb-px flex flex-wrap gap-x-8 gap-y-2"
                  aria-label="Tabs"
                >
                  {hasInvestorsView && (
                    <button
                      onClick={() => setActiveTab("investors")}
                      className={`py-3 px-2 border-b-2 font-medium text-base whitespace-nowrap ${
                        activeTab === "investors"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      Deleted Investors ({investorsTotal})
                    </button>
                  )}
                  {hasInrView && (
                    <button
                      onClick={() => setActiveTab("inr-investors")}
                      className={`py-3 px-2 border-b-2 font-medium text-base whitespace-nowrap ${
                        activeTab === "inr-investors"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      Deleted INR Investors ({inrInvestorsTotal})
                    </button>
                  )}
                </nav>
              </div>

              {/* Search */}
              <div className="p-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search deleted records..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searchLoading && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {availableTabs.length > 0 && selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-blue-900 font-medium">
              {selectedIds.size} record{selectedIds.size > 1 ? "s" : ""}{" "}
              selected
              <button
                onClick={clearSelection}
                disabled={bulkActionLoading}
                className="ml-3 text-xs text-blue-700 underline hover:text-blue-900 disabled:opacity-50"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkRestore}
                disabled={bulkActionLoading}
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Restore Selected
              </button>
              <button
                onClick={handleBulkPermanentDelete}
                disabled={bulkActionLoading}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                <TrashIcon className="h-4 w-4" />
                Delete Selected Permanently
              </button>
              {bulkActionLoading && (
                <div className="flex items-center gap-2 ml-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  {bulkProgress.total > 0 && (
                    <span className="text-sm text-blue-900">
                      {bulkProgress.done}/{bulkProgress.total}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        {availableTabs.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading deleted records...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {activeTab === "investors" ? (
                        <>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={allCurrentSelected}
                              onChange={toggleSelectAllCurrent}
                              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              aria-label="Select all on this page"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Mobile
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Scheme
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deleted At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deleted By
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={allCurrentSelected}
                              onChange={toggleSelectAllCurrent}
                              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              aria-label="Select all on this page"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Received
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Balance
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deleted At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deleted By
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activeTab === "investors"
                      ? investors.map(renderInvestorRow)
                      : inrInvestors.map(renderINRInvestorRow)}
                  </tbody>
                </table>

                {(activeTab === "investors" ? investors : inrInvestors)
                  .length === 0 && (
                  <div className="text-center py-8">
                    <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No deleted records
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No deleted records found for the current search criteria.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span>
                      Showing {(page - 1) * pageSize + 1} to{" "}
                      {Math.min(page * pageSize, total)} of {total} records
                    </span>
                    <label className="flex items-center gap-2">
                      <span className="text-gray-600">Per page:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                    </span>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page * pageSize >= total}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details Modal */}
        {renderDetailsModal()}
      </div>
    </div>
  );
};

export default DeletedRecordsPage;
